import type { DbAppointmentResult } from "@/types/database";
import type { AppointmentResult, AppointmentResultType } from "@/data/appointmentResultTypes";
import { getNextAction } from "@/data/appointmentResultTypes";

function deriveResultType(row: DbAppointmentResult): AppointmentResultType | null {
  switch (row.status) {
    case "completed":
      return row.result === "booked" ? "sold" : "no_sale";
    case "no_show":
      return "no_demo";
    case "cancelled":
      return "no_demo";
    case "rescheduled":
      return "rescheduled";
    default:
      return null; // 'scheduled' rows have no outcome yet — skip
  }
}

function deriveNoSaleReason(result: DbAppointmentResult["result"]): string | undefined {
  switch (result) {
    case "needs_more_time":
    case "deferred":
      return "not_ready";
    case "not_interested":
    case "wrong_number":
    case "voicemail":
      return "other";
    default:
      return undefined;
  }
}

function deriveNoDemoReason(status: DbAppointmentResult["status"]): string | undefined {
  if (status === "no_show") return "customer_not_home";
  if (status === "cancelled") return "other";
  return undefined;
}

export function dbAppointmentResultToAppointmentResult(
  row: DbAppointmentResult,
): AppointmentResult | null {
  const resultType = deriveResultType(row);
  if (!resultType) return null; // skip rows with no outcome

  const lead = row.leads;
  const meta = row.metadata;

  // Prefer stored granular reasons from metadata over derived values
  const noSaleReason = resultType === "no_sale"
    ? (meta?.noSaleReason ?? deriveNoSaleReason(row.result))
    : undefined;
  const noDemoReason = resultType === "no_demo"
    ? (meta?.noDemoReason ?? deriveNoDemoReason(row.status))
    : undefined;

// assigned_rep in DB is a profile UUID — use assigned_rep_name if the lead was enriched.
  // Fallback to "Unknown" if we only have a UUID or no name.
  const assignedRepName = lead?.assigned_rep_name;
  const assignedRep = (assignedRepName && !/^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(assignedRepName.trim()))
    ? assignedRepName
    : "Unknown";

  return {
    id: row.id,
    appointmentId: row.appointment_id,
    leadId: row.lead_id ?? "",
    opportunityId: row.quote_id ?? row.lead_id ?? "",
    customerName: row.contact_name || lead?.primary_name || "",
    assignedRep,
    source: lead?.lead_source || "",
    appointmentDatetime: row.scheduled_at,
    resultType,
    noSaleReason,
    noDemoReason,
    rescheduleDatetime: meta?.rescheduleDatetime,
    pricingSnapshot: meta?.pricingSnapshot,
    commissionEarnedAt: meta?.commissionEarnedAt,
    notes: row.notes ?? "",
    submittedAt: row.updated_at,
    submittedBy: row.created_by,
    nextAction: row.next_action ?? getNextAction(resultType, noSaleReason ?? noDemoReason),
    nextActionStatus: "completed",
  };
}
